const INK = '#121110'
const PAPER = '#F2EEE5'

/** Returns a 6-digit hex color, or the fallback when the value is empty or not a hex color. */
export const normalizeHex = (value: string | null | undefined, fallback = '#2B35FF'): string => {
    const hex = value?.trim() ?? ''

    if (/^#[0-9a-f]{6}$/i.test(hex)) return hex

    if (/^#[0-9a-f]{3}$/i.test(hex)) {
        return `#${hex.slice(1).split('').map(character => character + character).join('')}`
    }

    return fallback
}

const luminance = (hex: string): number => {
    const channels = [1, 3, 5].map(index => {
        const channel = parseInt(hex.slice(index, index + 2), 16) / 255
        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    })

    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

/** Blends two hex colors. `amount` is how much of `other` ends up in the result, from 0 to 1. */
export const mixHex = (base: string, other: string, amount: number): string => {
    const from = normalizeHex(base)
    const to = normalizeHex(other)

    const channels = [1, 3, 5].map(index => {
        const a = parseInt(from.slice(index, index + 2), 16)
        const b = parseInt(to.slice(index, index + 2), 16)
        return Math.round(a + (b - a) * amount).toString(16).padStart(2, '0')
    })

    return `#${channels.join('')}`.toUpperCase()
}

/** Picks ink or paper, whichever reads better on top of the given background color. */
export const readableTextOn = (background: string | null | undefined): string => {
    const value = luminance(normalizeHex(background))
    const withInk = (value + 0.05) / (luminance(INK) + 0.05)
    const withPaper = (luminance(PAPER) + 0.05) / (value + 0.05)

    return withInk >= withPaper ? INK : PAPER
}
